import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    FormGroup,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    Pagination,
    PaginationItem,
    PaginationLink,
    CardFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
    getAllRoomTypes,
    addRoomType,
    updateRoomType,
    deleteRoomType,
    toggleRoomTypeStatus,
    ROOM_TYPE_FOLDER,
    IMAGE_BASE_URL
} from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    }
};

const RoomTypes = () => {
    const [roomTypes, setRoomTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: "" });
    const [selectedFile, setSelectedFile] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All Status");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [nameError, setNameError] = useState("");
    const limit = 10;

    const toggle = () => {
        setModal(!modal);
        if (modal) {
            setEditingItem(null);
            setFormData({ name: "" });
            setSelectedFile(null);
            setNameError("");
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const statusParam = filterStatus === "All Status" ? "" : (filterStatus === "Active" ? "1" : "0");
            const res = await getAllRoomTypes(page, limit, searchTerm, statusParam);
            if (res.status === true) {
                setRoomTypes(res.payload.roomTypes || []);
                setTotal(res.payload.total || 0);
                setTotalPages(res.payload.totalPages || 1);
            }
        } catch (error) {
            console.error("Error fetching room types:", error);
            Swal.fire("Error", "Failed to load room types", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchTerm, filterStatus]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setNameError("");

        const nameValue = formData.name.trim();
        if (!nameValue) {
            setNameError("Type Name is required");
            return;
        }
        if (nameValue.length > 50) {
            setNameError("Type Name cannot exceed 50 characters");
            return;
        }
        const specialCharRegex = /[^a-zA-Z0-9 -]/;
        if (specialCharRegex.test(nameValue)) {
            setNameError("Type Name cannot contain special characters");
            return;
        }

        if (selectedFile) {
            const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
            if (!allowedTypes.includes(selectedFile.type)) {
                Swal.fire("Error", "Only image files allowed (e.g., JPG, JPEG, PNG, WEBP)", "error");
                return;
            }
        }

        try {
            const fd = new FormData();
            fd.append("rt_name", nameValue);
            if (selectedFile) {
                fd.append("rt_image", selectedFile);
            }

            console.log("Submitting Room Type FormData:");
            for (let pair of fd.entries()) {
                console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
            }

            if (editingItem) {
                const res = await updateRoomType(editingItem.rt_uuid, fd);
                if (res.status) {
                    setRoomTypes(prev => prev.map(item =>
                        item.rt_uuid === editingItem.rt_uuid ? { ...item, rt_name: formData.name, rt_image: res.payload.rt_image || item.rt_image } : item
                    ));
                }
            } else {
                const res = await addRoomType(fd);
                if (res.status) {
                    setRoomTypes(prev => [res.payload, ...prev.slice(0, limit - 1)]);
                    setTotal(prev => prev + 1);
                }
            }
            toggle();
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Failed to save room type", "error");
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({ name: item.rt_name });
        setModal(true);
    };

    const handleToggleStatus = async (id) => {
        try {
            const res = await toggleRoomTypeStatus(id);
            if (res.status) {
                // Update specific item status in state
                setRoomTypes(prev => prev.map(item =>
                    item.rt_uuid === id ? { ...item, rt_status: item.rt_status === "1" ? "0" : "1" } : item
                ));
            }
        } catch (error) {
            Swal.fire("Error", "Failed to update status", "error");
        }
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete Room Type?",
            text: "This will remove the room type from the active list.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await deleteRoomType(id);
                if (res.status) {
                    // Filter out item from state instantly
                    setRoomTypes(prev => prev.filter(item => item.rt_uuid !== id));
                    setTotal(prev => prev - 1);
                    // Optionally fetch data if page is now empty or to fill the gap
                    if (roomTypes.length <= 1 && page > 1) {
                        setPage(page - 1);
                    } else if (roomTypes.length <= 5) {
                        // Background sync if list getting thin
                        // fetchData(); 
                    }
                }
            } catch (error) {
                Swal.fire("Error", "Failed to delete room type", "error");
            }
        }
    };

    const renderPagination = () => {
        const totalPagesCount = Math.ceil(total / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2; // Number of pages to show on either side

        // Always show first page
        pages.push(
            <PaginationItem key={1} active={page === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        // Show start ellipsis if needed
        if (page > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        // Show pages around current
        const rangeStart = Math.max(2, page - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, page + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={page === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        // Show end ellipsis if needed
        if (page < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        // Always show last page
        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={page === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination className="pagination justify-content-end mb-0">
                <PaginationItem disabled={page === 1}>
                    <PaginationLink onClick={() => setPage(page - 1)} tabIndex="-1"><i className="fas fa-angle-left" /></PaginationLink>
                </PaginationItem>
                {pages}
                <PaginationItem disabled={page === totalPagesCount}>
                    <PaginationLink onClick={() => setPage(page + 1)}><i className="fas fa-angle-right" /></PaginationLink>
                </PaginationItem>
            </Pagination>
        );
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0" style={styles.glassCard}>
                            <CardHeader className="border-0 bg-transparent py-4">
                                <Row className="align-items-center">
                                    <Col md="4">
                                        <h3 className="mb-0 text-dark font-weight-700">
                                            <i className="ni ni-folder-17 text-info mr-2" /> Room Types Module
                                        </h3>
                                    </Col>
                                    <Col md="8">
                                        <div className="d-flex justify-content-md-end flex-wrap" style={{ gap: "10px" }}>
                                            <Input
                                                placeholder="Search room types..."
                                                type="text"
                                                className="form-control-alternative"
                                                style={{ width: "250px", borderRadius: "8px" }}
                                                value={searchTerm}
                                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                                            />
                                            <Input
                                                type="select"
                                                className="form-control-alternative"
                                                style={{ width: "150px", borderRadius: "8px" }}
                                                value={filterStatus}
                                                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                                            >
                                                <option>All Status</option>
                                                <option>Active</option>
                                                <option>Inactive</option>
                                            </Input>
                                            <Button color="primary" onClick={() => { setEditingItem(null); setFormData({ name: "" }); setModal(true); }} style={{ borderRadius: "10px" }}>
                                                <i className="fas fa-plus-circle mr-2" /> Add Room Type
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <Table className="align-items-center table-flush" hover>
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col" style={{ padding: "16px 24px" }}>Icon / Image</th>
                                                <th scope="col">Type Name</th>
                                                <th scope="col">Status</th>
                                                <th scope="col">Added On</th>
                                                <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading && roomTypes.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>
                                            ) : roomTypes.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-5 text-muted">No room types found.</td></tr>
                                            ) : (
                                                roomTypes.map((rt) => (
                                                    <tr key={rt.rt_uuid}>
                                                        <td style={{ padding: "16px 24px" }}>
                                                            <div className="avatar rounded-circle mr-3" style={{ width: "45px", height: "45px", background: "#f6f9fc", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "1px solid #eee" }}>
                                                                {rt.rt_image ? (
                                                                    <img
                                                                        src={`${IMAGE_BASE_URL}/${ROOM_TYPE_FOLDER}${rt.rt_image}`}
                                                                        alt={rt.rt_name}
                                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://via.placeholder.com/45?text=RT"; }}
                                                                    />
                                                                ) : (
                                                                    <i className="ni ni-image text-muted" style={{ fontSize: "1.2rem" }} />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td><span className="font-weight-600 text-dark">{rt.rt_name}</span></td>
                                                        <td>
                                                            <Badge
                                                                color={rt.rt_status === "1" ? "success" : "danger"}
                                                                pill
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => handleToggleStatus(rt.rt_uuid)}
                                                            >
                                                                {rt.rt_status === "1" ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </td>
                                                        <td>{new Date(rt.createdAt).toLocaleDateString()}</td>
                                                        <td className="text-right" style={{ paddingRight: "24px" }}>
                                                            <UncontrolledDropdown>
                                                                <DropdownToggle className="btn-icon-only" role="button" size="sm" color="">
                                                                    <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                                </DropdownToggle>
                                                                <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                    <DropdownItem onClick={() => handleEdit(rt)}>Edit</DropdownItem>
                                                                    <DropdownItem onClick={() => handleToggleStatus(rt.rt_uuid)}>
                                                                        {rt.rt_status === "1" ? "Deactivate" : "Activate"}
                                                                    </DropdownItem>
                                                                    <DropdownItem divider />
                                                                    <DropdownItem onClick={() => handleDelete(rt.rt_uuid)} className="text-danger">Delete</DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                <span className="text-muted small">
                                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
                                </span>
                                {renderPagination()}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle} className="border-0">
                    {editingItem ? "Rename Room Type" : "Add New Room Type"}
                </ModalHeader>
                <ModalBody>
                    <form onSubmit={handleSubmit} noValidate>
                        <FormGroup>
                            <label className="form-control-label">Type Name</label>
                            <Input
                                type="text"
                                placeholder="e.g. Professional Suite"
                                value={formData.name}
                                invalid={!!nameError}
                                onChange={(e) => {
                                    setFormData({ ...formData, name: e.target.value });
                                    if (nameError) setNameError("");
                                }}
                                style={{ borderRadius: "8px", minHeight: "45px" }}
                                autoFocus
                            />
                            {nameError && (
                                <small className="text-danger mt-1 d-block">{nameError}</small>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <label className="form-control-label">Type Image (Optional)</label>
                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setSelectedFile(e.target.files[0])}
                                className="form-control-alternative"
                                style={{ padding: "10px", borderRadius: "8px" }}
                            />
                            {editingItem && editingItem.rt_image && (
                                <small className="text-muted d-block mt-2">
                                    Current image: {editingItem.rt_image}
                                </small>
                            )}
                        </FormGroup>
                        <Button color="primary" block type="submit" style={{ minHeight: "45px", borderRadius: "10px" }}>
                            {editingItem ? "Update" : "Save"} Room Type
                        </Button>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default RoomTypes;
